import React, { useState } from 'react';
import * as Yup from 'yup';
import { FormattedMessage, useIntl } from 'react-intl';
import PasswordField from '@/components/form/password/PasswordField';
import { Button } from '@/components/buttons/Button';
import Form from '@/components/form/Form';
import {
  HomeHeroAuthenticationStep,
  LoginFlowVariant,
  useHomeHeroAuthenticationStepActions,
} from '@/store/authentication/AuthenticationStore';
import { useDispatch, useSelector } from 'react-redux';
import { loginSuccess } from '@/store/actions/auth';
import { GTagEvents, OpenedFrom, triggerGTag } from '@/lib/gTagManager';
import PromptMessage from '@/components/UI/PromptMessage';
import PhoneNumberField from '@/components/form/phone-number/PhoneNumberField';
import { MobileInputModel } from '@/models/mobile-input.model';
import { scrollToTop } from '@/utils/utils';
import { BtnBlueLink } from '@/components/buttons/BtnBlueLink';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';
import { useRegistration } from '@/hooks/useRegistration';
import StorageManager, { ReturningUserLoginMethod } from '@/shared/storageManager';
import BffApiService from '@/shared/bffApiService';
import { useModal } from '@/features/modal/useModal';
import { MODALS } from '@/features/modal/types';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import { BffAuthResponse, BlockReasonType } from '@lucky7ventures/bff-types';
import { BffApiError } from '@/models/bff-api-error';
import { selectIsCanadaMarket } from '@/store/selectors/commonSelectors';

const initialValues = {
  mobile: {
    number: '',
    country: 'TH',
    prefix: '+66',
    wholeNumber: '',
  },
  password: '',
};

const LoginViaSmsForm = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { openModal } = useModal();
  const { loading, error, request } = useBffApiRequest<BffAuthResponse>();
  const navigate = useAbsoluteNavigate();
  const { openRegistration, isRegistrationRemoved } = useRegistration();
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [errorMessage, setErrorMessage] = useState<React.ReactNode | null>(null);
  const isCanadaMarket = useSelector(selectIsCanadaMarket);

  const validationSchema = Yup.object().shape({
    mobile: Yup.object().shape({
      wholeNumber: Yup.string().required(intl.formatMessage({ id: 'input.error.required' })),
      number: Yup.string().min(5, intl.formatMessage({ id: 'inputs.mobile.invalid' })),
    }),
    password: Yup.string()
      .required(intl.formatMessage({ id: 'input.error.required' }))
      .trim(),
  });

  const { setLoginFlowVariant, setAuthenticationCredentials, setHomeHeroAuthenticationStep } =
    useHomeHeroAuthenticationStepActions();

  const openRegistrationClick = () => {
    openRegistration({ openedFrom: OpenedFrom.LoginViaSms });
  };

  const forgotPasswordClick = () => {
    triggerGTag(GTagEvents.forgot_password_sms_click);
    navigate('/forgot-password-sms');
  };

  const switchToEmailLogin = () => {
    setLoginFlowVariant(LoginFlowVariant.Email);
    scrollToTop();
  };

  const successCallback = (data: BffAuthResponse, username: string, password: string) => {
    if (isCanadaMarket && data.blockReason?.blockReasonType === BlockReasonType.Timeout) {
      setErrorMessage(intl.formatMessage({ id: 'login.error.timeout' }));
      return;
    }

    StorageManager.setIsReturningUser(ReturningUserLoginMethod.Mobile);
    if (data.result === 'SUCCESSFUL') {
      dispatch(loginSuccess(data.token, { blockReason: data.blockReason }));
      triggerGTag(GTagEvents.login_success);
    } else if (data.result === 'TWO_FACTOR_FIRST_STEP_SUCCESSFUL') {
      setAuthenticationCredentials(username, password);
      setHomeHeroAuthenticationStep(HomeHeroAuthenticationStep.TwoFactorAuthentication);
      triggerGTag(GTagEvents.login_2fa);
    }
  };

  const errorCallback = (error: BffApiError) => {
    setLoginAttempts(prevState => prevState + 1);

    if (loginAttempts > 1) {
      openModal(MODALS.FAILED_LOGIN_SMS);
    }

    triggerGTag(GTagEvents.login_error, {
      errorId: error.code,
    });
  };

  const handleSubmit = ({ mobile, password }: { mobile: MobileInputModel; password: string }) => {
    setErrorMessage(null);
    request({
      apiMethod: BffApiService.login,
      payload: {
        username: mobile.wholeNumber,
        password,
      },
      errorCallback: errorCallback,
      successCallback: data => successCallback(data, mobile.wholeNumber, password),
    });
  };

  return (
    <Form
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      className="flex flex-col w-full justify-between gap-7"
    >
      {({ isValid }) => (
        <>
          <PhoneNumberField
            label={intl.formatMessage({ id: 'inputs.mobile' })}
            name="mobile"
            onFocus={() => triggerGTag(GTagEvents.login_via_sms_mobile_focus)}
            onValidationError={error =>
              triggerGTag(GTagEvents.login_via_sms_mobile_error, { error })
            }
            className="border-[1px] border-white/30 text-white focus-within:border-blue-light
                [&_input]:placeholder:!text-white/60"
          />
          <PasswordField
            data-cy="login-form-password"
            label={intl.formatMessage({ id: 'inputs.password' })}
            name="password"
            className="text-white border-[1px] border-transparent focus-within:border-blue-blue bg-white/20"
          />
          <div className="grid grid-cols-1 gap-4 mt-4">
            {(error || errorMessage) && (
              <PromptMessage
                type="error"
                message={errorMessage || intl.formatMessage({ id: 'error.1' })}
              />
            )}
            <Button
              text={intl.formatMessage({ id: 'authentication.login.login-button' })}
              type="submit"
              btnType="primary"
              data-cy="login-form-submit-button"
              isLoading={loading}
              disabled={loading || !isValid}
            />
            {!isRegistrationRemoved && (
              <div className="text-white text-md text-center">
                <FormattedMessage
                  id="authentication.login.change-to-register"
                  values={{
                    a: str => (
                      <span
                        data-cy="login-form-switch-registration-button"
                        onClick={openRegistrationClick}
                        className="text-blue-lighter font-bold underline cursor-pointer"
                      >
                        {str}
                      </span>
                    ),
                  }}
                />
              </div>
            )}
            <div className="text-xs italic text-white">
              <FormattedMessage
                id="authentication.login.forgot-password"
                values={{
                  a: str => (
                    <span
                      data-cy="login-form-switch-forgot-password-button"
                      onClick={forgotPasswordClick}
                      className="text-blue-lighter font-bold underline cursor-pointer"
                    >
                      {str}
                    </span>
                  ),
                }}
              />
            </div>
            <div className="h-[1px] bg-white w-full opacity-60"></div>
            <BtnBlueLink
              data-cy="login-form-switch-login-via-email"
              onClickHandler={switchToEmailLogin}
              text={intl.formatMessage({ id: 'authentication.login.via-email' })}
            />
          </div>
        </>
      )}
    </Form>
  );
};

export default LoginViaSmsForm;
